<template>
    <div>
        <validation-observer ref="validationObserver" v-slot="vo">
            <b-form-row>
                <validation-provider rules="required" name="Ordering Client" v-slot="vp" slim>
                    <b-form-group label="Ordering Client"
                                  label-for="orderingClientId"
                                  class="col-md-4">
                        <select2 name="orderingClientId"
                                 :disabled="readonly || (order.confirmedDateTimeUTC != null) || !canEditOrder || (order.clientOrderRequestId != null) || isICMS"
                                 v-model="orderingClientId"
                                 :options="selectableClients"
                                 placeholder="Select a client"
                                 :state="vp.errors[0] ? false : null"
                                 v-on:select="orderingClientSelected">
                        </select2>
                        <b-form-invalid-feedback>{{ vp.errors[0] }}</b-form-invalid-feedback>
                    </b-form-group>
                </validation-provider>
                <validation-provider rules="required" name="Ordering Client Contact" v-slot="vp" slim>
                    <b-form-group label="Ordering Client Contact"
                                  label-for="orderingClientContactId"
                                  class="col-md-4">
                        <contact-select name="orderingClientContactId"
                                        :disabled="readonly || !canEditOrder || (order.clientOrderRequestId != null) || isICMS"
                                        :api-url="apiControllerUrl('contact/client')"
                                        :ref-data-api-controller-url="apiControllerUrl('referencedata')"
                                        :mandatory-contact-properties="mandatoryContactProperties"
                                        :client-id="orderingClientId"
                                        v-model="orderingClientContactId"
                                        :initial-contacts="initialOrderingClientContact ? [ initialOrderingClientContact ] : null"
                                        :state="isUuidNullOrEmpty(orderingClientId) ? null : vp.errors[0] ? false : null">
                        </contact-select>
                        <b-form-invalid-feedback :state="isUuidNullOrEmpty(orderingClientId) ? null : vp.errors[0] ? false : null">{{ vp.errors[0] }}</b-form-invalid-feedback>
                    </b-form-group>
                </validation-provider>
                <b-form-group label="Client Reference"
                              label-for="clientReference"
                              class="col-md-2">
                    <b-form-input name="clientReference"
                                  id="clientReference"
                                  maxlength="500"
                                  :disabled="readonly || !canEditOrder || (order.clientOrderRequestId != null)"
                                  v-model="clientReference">
                    </b-form-input>
                    <b-tooltip target="clientReference" triggers="hover" placement="bottom" variant="dark" v-if="clientReference">
                        {{ clientReference }}
                    </b-tooltip>
                </b-form-group>
                <validation-provider rules="required" name="Ordered At" v-slot="vp" slim>
                    <b-form-group label="Ordered At"
                                  label-for="orderedDateTimeUTC"
                                  class="col-md-2">
                        <ejs-datetimepicker name="orderedDateTimeUTC"
                                            v-model="orderedDateTimeUTC"
                                            format="d/M/yyyy h:mma"
                                            :disabled="readonly || (order.confirmedDateTimeUTC != null) || !canEditOrder || (order.clientOrderRequestId != null) || isICMS">
                        </ejs-datetimepicker>
                        <b-form-invalid-feedback :state="vp.errors[0] ? false : null">{{ vp.errors[0] }}</b-form-invalid-feedback>
                    </b-form-group>
                </validation-provider>
            </b-form-row>
            <b-form-row>
                <validation-provider ref="orderingClientBranchValidator" rules="required" name="Client Branch" v-slot="vp" slim>
                    <b-form-group label="Client Branch"
                                  label-for="orderingClientBranchId"
                                  class="col-md-4">
                        <select2 ref="orderingClientBranchSelect"
                                 name="orderingClientBranchId"
                                 v-model="orderingClientBranchId"
                                 :options="selectableClientBranches"
                                 key-property="clientBranchId"
                                 text-property="name"
                                 :disabled="readonly || !canEditOrder || (order.clientOrderRequestId != null) || (orderingClientId == null) || isICMS"
                                 :placeholder="isUuidNullOrEmpty(orderingClientId) ? 'Select a client first' : 'Select a client branch'"
                                 :state="vp.errors[0] ? false : null">
                        </select2>
                        <b-form-invalid-feedback>{{ vp.errors[0] }}</b-form-invalid-feedback>
                    </b-form-group>
                </validation-provider>
                <b-form-group class="col-md-4">
                    <b-form-checkbox name="separateBillingContact"
                                     v-model="separateBillingContact"
                                     class="mb-2"
                                     switch
                                     :disabled="readonly || !canEditOrder">
                        Separate billing contact
                    </b-form-checkbox>
                    <validation-provider rules="required" name="Billing Contact" v-slot="vp" v-if="separateBillingContact">
                        <contact-select name="billingContactId"
                                        :disabled="readonly || !canEditOrder"
                                        :api-url="apiControllerUrl('contact/billing')"
                                        :ref-data-api-controller-url="apiControllerUrl('referencedata')"
                                        :mandatory-contact-properties="mandatoryContactProperties"
                                        :client-id="orderingClientId"
                                        v-model="billingContactId"
                                        :initial-contacts="initialBillingContact ? [ initialBillingContact ] : null"
                                        :state="isUuidNullOrEmpty(orderingClientId) ? null : vp.errors[0] ? false : null">
                        </contact-select>
                        <b-form-invalid-feedback :state="isUuidNullOrEmpty(orderingClientId) ? null : vp.errors[0] ? false : null">{{ vp.errors[0] }}</b-form-invalid-feedback>
                    </validation-provider>
                </b-form-group>
                <b-form-group label="Order Number"
                              label-for="orderNumber"
                              class="col-md-2">
                    <b-form-input name="orderNumber"
                                  :value="isUuidNullOrEmpty(order.orderId) ? null : order.orderNumberDisplayText"
                                  readonly>
                    </b-form-input>
                </b-form-group>
                <b-form-group label="Order Status"
                              label-for="orderStatus"
                              class="col-md-2">
                    <b-form-input name="orderStatus"
                                  :value="isUuidNullOrEmpty(order.orderId) ? null : order.statusDisplayName"
                                  readonly>
                    </b-form-input>
                </b-form-group>
            </b-form-row>
            <slot></slot>
            <b-form-row v-if="!hideServiceTypeAndDelaySelection">
                <validation-provider ref="serviceTypeSelectValidator" rules="required" name="Service Type" v-slot="vp" slim>
                    <b-form-group label="Service Type"
                                  label-for="serviceTypeId"
                                  class="col-md-4">
                        <select2 ref="serviceTypeSelect"
                                 name="serviceTypeId"
                                 :disabled="readonly || (order.confirmedDateTimeUTC != null) || !canEditOrder || (currentSelectableServiceTypes == null)"
                                 v-model="serviceTypeId"
                                 :options="currentSelectableServiceTypes"
                                 key-property="serviceTypeId"
                                 text-property="name"
                                 :placeholder="(currentSelectableServiceTypes == null) ? serviceTypesNullSelectPlaceholder : 'Select a service type'"
                                 v-on:select="serviceTypeSelected"
                                 :state="(currentSelectableServiceTypes == null) ? null : vp.errors[0] ? false : null">
                        </select2>
                        <b-form-invalid-feedback :state="(currentSelectableServiceTypes == null) ? null : vp.errors[0] ? false : null">{{ vp.errors[0] }}</b-form-invalid-feedback>
                    </b-form-group>
                </validation-provider>
                <validation-provider rules="required" name="Turnaround" v-slot="vp" slim v-if="serviceTypeRequiresServiceDelay">
                    <b-form-group label="Turnaround"
                                  label-for="serviceDelayId"
                                  class="col-md-4">
                        <select2 ref="serviceDelaySelect"
                                 name="serviceDelayId"
                                 :disabled="readonly || (order.confirmedDateTimeUTC != null) || !canEditOrder"
                                 v-model="serviceDelayId"
                                 :options="currentSelectableServiceDelays"
                                 key-property="serviceDelayId"
                                 text-property="name"
                                 placeholder="Select a service delay"
                                 :state="vp.errors[0] ? false : null">
                        </select2>
                        <b-form-invalid-feedback :state="vp.errors[0] ? false : null">{{ vp.errors[0] }}</b-form-invalid-feedback>
                    </b-form-group>
                </validation-provider>
            </b-form-row>

            <slot name="after-service-selection"></slot>

            <b-form-row v-if="$config.enableTranscriptDeliveryOptions">
                <b-form-group label="Transcript Delivery Options"
                              label-for="transcriptDeliveryOptions"
                              class="col">
                    <select2 name="transcriptDeliveryOptions"
                             multiple
                             :options="allTranscriptDeliveryOptions"
                             :disabled="readonly || (order.confirmedDateTimeUTC != null) || !canEditOrder || isICMS"
                             key-property="value"
                             text-property="text"
                             v-model="transcriptDeliveryOptionsList">
                    </select2>
                </b-form-group>
            </b-form-row>

            <non-production-charges :all-non-production-charge-types="currentSelectableNonProductionChargeTypes"
                                    :all-possible-dates="currentPossibleNonProductionChargeDates"
                                    :use-client-rate="orderingClientManagesAssignedContracts"
                                    v-model="currentNonProductionCharges"
                                    class="mt-2 mb-3"
                                    :readonly="readonly || !canEditOrder"
                                    v-if="(currentSelectableNonProductionChargeTypes != null) && (currentSelectableNonProductionChargeTypes.length > 0)">
            </non-production-charges>

            <contacts label="Default Distribution List" :contacts="defaultDeliveryContacts" v-if="defaultDeliveryContacts && defaultDeliveryContacts.length && !hideDefaultDistributionList && !serviceTypeHideDistributionList"></contacts>
            <b-form-row v-if="!hideDistributionList && !serviceTypeHideDistributionList">
                <validation-provider rules="required" name="Distribution List" v-slot="vp" slim>
                    <b-form-group label="Distribution List"
                                  label-for="deliveryContactIds"
                                  class="col">
                        <contact-select name="deliveryContactIds"
                                        :api-url="apiControllerUrl('contact/delivery')"
                                        :ref-data-api-controller-url="apiControllerUrl('referencedata')"
                                        :client-id="orderingClientId"
                                        :disabled="readonly || !canEditOrder"
                                        v-model="currentDeliveryContactIds"
                                        :initial-contacts="initialDeliveryContacts"
                                        multiple
                                        :state="isUuidNullOrEmpty(orderingClientId) ? null : vp.errors[0] ? false : null">
                        </contact-select>
                        <b-form-invalid-feedback :state="isUuidNullOrEmpty(orderingClientId) ? null : vp.errors[0] ? false : null">{{ vp.errors[0] }}</b-form-invalid-feedback>
                    </b-form-group>
                </validation-provider>
            </b-form-row>
            <b-form-row v-if="!hideDistributionList && !serviceTypeHideDistributionList">
                <validation-provider rules="" name="CC List" v-slot="vp" slim>
                    <b-form-group label="Distribution CC List"
                                  label-for="deliveryContactCcIds"
                                  class="col">
                        <contact-select name="deliveryContactCcIds"
                                        :api-url="apiControllerUrl('contact/delivery')"
                                        :ref-data-api-controller-url="apiControllerUrl('referencedata')"
                                        :client-id="orderingClientId"
                                        :disabled="readonly || !canEditOrder"
                                        v-model="currentDeliveryContactCcIds"
                                        :initial-contacts="initialDeliveryCcContacts"
                                        multiple
                                        :state="isUuidNullOrEmpty(orderingClientId) ? null : vp.errors[0] ? false : null">
                        </contact-select>
                        <b-form-invalid-feedback :state="isUuidNullOrEmpty(orderingClientId) ? null : vp.errors[0] ? false : null">{{ vp.errors[0] }}</b-form-invalid-feedback>
                    </b-form-group>
                </validation-provider>
            </b-form-row>
            <b-form-row v-if="!hideDistributionList && !serviceTypeHideDistributionList">
                <validation-provider rules="" name="BCC List" v-slot="vp" slim>
                    <b-form-group label="Distribution BCC List"
                                  label-for="deliveryContactBccIds"
                                  class="col">
                        <contact-select name="deliveryContactBccIds"
                                        :api-url="apiControllerUrl('contact/delivery')"
                                        :ref-data-api-controller-url="apiControllerUrl('referencedata')"
                                        :client-id="orderingClientId"
                                        :disabled="readonly || !canEditOrder"
                                        v-model="currentDeliveryContactBccIds"
                                        :initial-contacts="initialDeliveryBccContacts"
                                        multiple
                                        :state="isUuidNullOrEmpty(orderingClientId) ? null : vp.errors[0] ? false : null">
                        </contact-select>
                        <b-form-invalid-feedback :state="isUuidNullOrEmpty(orderingClientId) ? null : vp.errors[0] ? false : null">{{ vp.errors[0] }}</b-form-invalid-feedback>
                    </b-form-group>
                </validation-provider>
            </b-form-row>


            <b-form-row v-if="emailsLink && !hideDistributionList && !serviceTypeHideDistributionList">
                <b-form-group>
                    <b-button :href="emailsLink">Create Email</b-button>
                </b-form-group>
            </b-form-row>

            <comments :api-controller-url="apiControllerUrl('comment')"
                      :current-user-account-id="currentUserAccountId"
                      :order-id="order.orderId"
                      v-model="newOrderComments"
                      :can-create-comment="canCreateComment"
                      :can-create-billing-information-comment="canCreateBillingInformationComment"
                      :can-edit-and-retract-all-comments="canEditAndRetractAllComments"
                      :disabled="readonly || !canEditOrder"
                      :default-comment-type="$enum.CommentType.Order"
                      class="my-2">
            </comments>

            <attachments v-model="currentAttachments"
                         :all-attachment-classifications="allAttachmentClassifications"
                         :disabled="readonly || !canEditOrder"
                         v-if="canViewAttachments"
                         attachment-classification-selection="required"
                         :can-add-attachments="(allAttachmentClassifications != null) && (allAttachmentClassifications.length > 0)">
            </attachments>

            <b-form-row v-if="displayAsrOptions">
                <label class="col-12"><b>ASR Options:</b></label>
                <b-form-group class="col-2">
                    <b-form-row>
                        <label class="col-12">Asr Engine:</label>
                        <b-form-group class="col-12">
                            <select2 v-model="asrEngine"
                                     placeholder="Select ASR Engine"
                                     :options="allAsrEngineOptions"
                                     disable-search
                                     css-class="form-control form-control-sm">
                            </select2>
                        </b-form-group>
                    </b-form-row>
                </b-form-group>

                <b-form-group class="col-6">
                    <b-form-row v-if="valueExists([asrOptions.enableSpeakerSeparation, asrOptions.combineTranscriptResult])">
                        <label class="col-12">Transcript Options:</label>
                        <b-form-group v-if="valueExists([asrOptions.enableSpeakerSeparation])" class="col-6">
                            <b-form-checkbox name="enableSpeakerSeparation"
                                             v-model="asrOptions.enableSpeakerSeparation"
                                             class="mb-2"
                                             switch>
                                Enable Speaker Separation
                            </b-form-checkbox>
                        </b-form-group>

                        <b-form-group v-if="valueExists([asrOptions.combineTranscriptResult])" class="col-6">
                            <b-form-checkbox name="combineTranscriptResult"
                                             v-model="asrOptions.combineTranscriptResult"
                                             class="mb-2"
                                             switch>
                                Combine Transcript Result
                            </b-form-checkbox>
                        </b-form-group>
                    </b-form-row>
                </b-form-group>

                <b-form-group class="col-3">
                    <b-form-row v-if="valueExists([asrOptions.dictatedPunctuationMode, asrOptions.automaticPunctuationMode])">
                        <label class="col-12">Punctuation Mode:</label>
                        <b-form-group v-if="valueExists([asrOptions.dictatedPunctuationMode])" class="col-6">
                            <b-form-checkbox name="dictatedPunctuationMode"
                                             v-model="asrOptions.dictatedPunctuationMode"
                                             class="mb-2"
                                             switch disabled>
                                Dictated
                            </b-form-checkbox>
                        </b-form-group>
                        <b-form-group v-if="valueExists([asrOptions.automaticPunctuationMode])" class="col-6">
                            <b-form-checkbox name="automaticPunctuationMode"
                                             v-model="asrOptions.automaticPunctuationMode"
                                             class="mb-2"
                                             switch disabled>
                                Automatic
                            </b-form-checkbox>
                        </b-form-group>
                    </b-form-row>
                </b-form-group>

                <b-form-group v-if="valueExists([asrOptions.profanityMode])" class="col-2">
                    <b-form-row>
                        <label class="col-12">Profanity Filter Mode:</label>
                        <b-form-group class="col-12">
                            <select2 v-model="asrOptions.profanityMode"
                                     placeholder="Select How to Handle Profanity"
                                     :options="profanityOptions"
                                     disable-search
                                     css-class="form-control form-control-sm">
                            </select2>
                        </b-form-group>
                    </b-form-row>
                </b-form-group>

            </b-form-row>

            <order-payments v-model="newOrderPayments"
                            :api-base-url="apiBaseUrl"
                            :order-id="order.orderId"
                            :court-registry-id="order.courtRegistryId"
                            :interview-organisation-id="order.interviewOrganisationId"
                            :effective-ordered-date="order.effectiveOrderedDateTimeUTC ? formatDateTime(order.effectiveOrderedDateTimeUTC, 'YYYY-MM-DD', true, true) : null"
                            @change="updateDepositSubStatus">
            </order-payments>

            <b-form-group label="Credit Amount"
                          label-for="credit-amount"
                          class="mt-2 w-25"
                          content-cols="6">
                <currency-input id="credit-amount"
                                v-model="creditAmount"
                                :locale="$config.currentLocale"
                                :currency="currencyCode"
                                :precision="2"
                                class="text-right form-control form-control"
                                :disabled="!canEditOrder">
                </currency-input>
            </b-form-group>

            <validation-summary ref="validationSummary" :vee-validate-observer-errors="vo.errors"></validation-summary>

            <div class="btn-toolbar justify-content-end mt-2" role="toolbar" aria-label="Action buttons">
                <div class="mb-1" v-if="canViewSubStatusDetails">
                    <b-form-select v-model="selectedAudioSubStatus" :options="allAudioSubStatusOptions" size="sm" style="width: 200px;" class="mr-1" :disabled="readonly || !canEditOrder || !canModifyAudioSubStatus"></b-form-select>
                    <b-form-select v-model="selectedLogNotesSubStatus" :options="allLogNotesSubStatusOptions" size="sm" style="width: 200px;" class="mr-1" :disabled="readonly || !canEditOrder || !canModifyLogNotesSubStatus"></b-form-select>
                    <b-form-select v-model="selectedReleaseApprovalSubStatus" :options="allReleaseApprovalSubStatusOptions" size="sm" style="width: 200px;" class="mr-1" :disabled="readonly || !canEditOrder || !canModifyReleaseApprovalSubStatus"></b-form-select>
                    <b-form-select v-model="selectedDepositSubStatus" :options="allDepositSubStatusOptions" size="sm" style="width: 200px;" class="mr-1" :disabled="readonly || !canEditOrder || !canModifyDepositSubStatus"></b-form-select>
                    <b-form-select v-model="selectedVettingSubStatus" :options="allVettingSubStatusOptions" size="sm" style="width: 200px;" :disabled="readonly || !canEditOrder || !canModifyVettingSubStatus"></b-form-select>
                </div>
                <div class="btn-group ml-2" role="group">
                    <b-button variant="outline-success" v-on:click.prevent="trySaveOrder('edit')" v-if="!readonly && canEditOrder"><i class="fas fa-check mr-1"></i>Save and Continue</b-button>
                    <b-button variant="outline-success" v-on:click.prevent="trySaveOrder('new')" v-if="!readonly && canEditOrder"><i class="fas fa-check mr-1"></i>Save and Create New</b-button>
                    <b-button id="saveAndCloseButton" variant="outline-success" v-on:click.prevent="trySaveOrder('close')" v-if="!readonly && canEditOrder"><i class="fas fa-check mr-1"></i>Save and Close</b-button>
                    <b-button :href="returnUrl" :variant="(readonly || !canEditOrder) ? 'outline-secondary' : 'outline-danger'"><i :class="(readonly || !canEditOrder) ? 'fas fa-arrow-left mr-1' : 'fas fa-times mr-1'"></i>{{ (readonly || !canEditOrder) ? 'Back' : 'Cancel' }}</b-button>
                </div>
            </div>
        </validation-observer>
        <b-input-group size="sm" prepend="Created at" class="mt-2" v-if="!isUuidNullOrEmpty(order.orderId)">
            <b-form-input :value="order.createdDateTimeUTC | formatDateTime('DD/MM/YYYY h:mmA')" readonly></b-form-input>
            <b-input-group-prepend is-text class="input-group-append">By</b-input-group-prepend>
            <b-form-input :value="(order.createdByUserAccount != null) ? order.createdByUserAccount.displayName : null" readonly></b-form-input>
            <b-input-group-prepend is-text class="input-group-append">Last update at</b-input-group-prepend>
            <b-form-input :value="order.lastUpdateDateTimeUTC | formatDateTime('DD/MM/YYYY h:mmA')" readonly></b-form-input>
            <b-input-group-prepend is-text class="input-group-append">By</b-input-group-prepend>
            <b-form-input :value="(order.lastUpdateByUserAccount != null) ? order.lastUpdateByUserAccount.displayName : null" readonly></b-form-input>
        </b-input-group>
    </div>
</template>

<script>
    //import $ from 'jquery';
    import Vue from 'vue';
    import BootstrapVue from 'bootstrap-vue';
    import axios from 'axios';
    import { DateTimePickerPlugin } from '@syncfusion/ej2-vue-calendars';

    import { ValidationProvider, ValidationObserver } from 'vee-validate';

    import { extend, localize } from 'vee-validate';
    import { required } from 'vee-validate/dist/rules';
    import en from 'vee-validate/dist/locale/en.json';
    extend('required', required);
    localize('en', en);

    import Select2 from '../Select2';
    import ErrorsMixin from '../../mixins/ErrorsMixin';
    import DateTimeMixin from '../../mixins/DateTimeMixin';
    import UuidMixins from '../../mixins/UuidMixins';

    import ContactSelect from '../ContactSelect.vue'
    import NonProductionCharges from '../NonProductionCharges.vue'
    import Contacts from '../Contacts.vue'
    import Attachments from '../Attachments.vue'
    import OrderPayments from '../OrderPayments.vue'
    import Comments from '../Comments.vue'
    import ValidationSummary from '../ValidationSummary.vue'
    import VueCurrencyInput from 'vue-currency-input'

    Vue.use(BootstrapVue);
    Vue.use(DateTimePickerPlugin);
    Vue.use(VueCurrencyInput);
    Vue.component('select2', Select2);
    Vue.component('validation-provider', ValidationProvider);
    Vue.component('validation-observer', ValidationObserver);
    Vue.component('validation-summary', ValidationSummary);
    Vue.component('contact-select', ContactSelect);
    Vue.component('non-production-charges', NonProductionCharges);
    Vue.component('contacts', Contacts);
    Vue.component('attachments', Attachments);
    Vue.component('order-payments', OrderPayments);
    Vue.component('comments', Comments);

    export default {
        name: 'GeneralOrderDetails',
        mixins: [ErrorsMixin, DateTimeMixin, UuidMixins],
        filters: DateTimeMixin.filter,
        props: {
            returnUrl: {
                type: String,
                required: true
            },
            createOrderUrl: {
                type: String,
                required: true
            },
            editOrderUrlTemplate: {
                type: String,
                required: true
            },
            apiBaseUrl: {
                type: String,
                required: true
            },
            fileDownloadUrl: {
                type: String,
                required: true
            },
            currentUserAccountId: {
                type: String,
                required: true
            },
            order: {
                type: Object,
                required: true
            },
            selectableClients: {
                type: Array,
                default: null
            },
            initialClientManagesAssignedContracts: {
                type: Boolean,
                default: false
            },
            initialClientBranches: {
                type: Array,
                default: null
            },
            initialOrderingClientContact: {
                type: Object,
                default: null
            },
            initialBillingContact: {
                type: Object,
                default: null
            },
            promptForBillingContact: {
                type: Boolean,
                default: true
            },
            selectableServiceTypes: {
                type: Array,
                default: null
            },
            serviceTypesNullSelectPlaceholder: {
                type: String,
                default: null
            },
            selectableServiceDelays: {
                type: Array,
                default: null
            },
            selectableNonProductionChargeTypes: {
                type: Array,
                default: null
            },
            possibleNonProductionDates: {
                type: Array,
                default: null
            },
            nonProductionCharges: {
                type: Array,
                default: null
            },
            defaultDeliveryContacts: {
                type: Array,
                default: null
            },
            initialDeliveryContacts: {
                type: Array,
                default: null
            },
            initialDeliveryCcContacts: {
                type: Array,
                default: null
            },
            initialDeliveryBccContacts: {
                type: Array,
                default: null
            },
            deliveryContactIds: {
                type: Array,
                default: null
            },
            deliveryContactCcIds: {
                type: Array,
                default: null
            },
            deliveryContactBccIds: {
                type: Array,
                default: null
            },
            emailsLink: {
                type: String,
                default: null
            },
            allAttachmentClassifications: {
                type: Array,
                required: true
            },
            allTranscriptDeliveryOptions: {
                type: Array,
                required: true
            },
            attachments: {
                type: Array,
                default: null
            },
            allAudioSubStatusOptions: {
                type: Array,
                default: null
            },
            allLogNotesSubStatusOptions: {
                type: Array,
                default: null
            },
            allReleaseApprovalSubStatusOptions: {
                type: Array,
                default: null
            },
            allDepositSubStatusOptions: {
                type: Array,
                default: null
            },
            allVettingSubStatusOptions: {
                type: Array,
                default: null
            },
            canEditOrder: {
                type: Boolean,
                requird: true
            },
            canViewAttachments: {
                type: Boolean,
                requird: true
            },
            canCreateComment: {
                type: Boolean,
                default: false
            },
            canCreateBillingInformationComment: {
                type: Boolean,
                default: false
            },
            canEditAndRetractAllComments: {
                type: Boolean,
                default: false
            },
            canViewSubStatusDetails: {
                type: Boolean,
                default: false
            },
            canModifyAudioSubStatus: {
                type: Boolean,
                default: false
            },
            canModifyLogNotesSubStatus: {
                type: Boolean,
                default: false
            },
            canModifyReleaseApprovalSubStatus: {
                type: Boolean,
                default: false
            },
            canModifyDepositSubStatus: {
                type: Boolean,
                default: false
            },
            canModifyVettingSubStatus: {
                type: Boolean,
                default: false
            },
            readonly: {
                type: Boolean,
                default: false
            },
            save: {
                type: Function,
                required: true
            },
            validate: {
                type: Function,
                default: null
            },
            initialTranscriptDeliveryOptions: {
                type: Array,
                required: false

            },
            hideDistributionList: {
                type: Boolean,
                default: false
            },
            hideDefaultDistributionList: {
                type: Boolean,
                default: false
            },
            hideServiceTypeAndDelaySelection: {
                type: Boolean,
                default: false
            },
            currencyCode: {
                type: String,
                required: true
            },
        },
        data: function () {
            return {

                //transcriptDeliveryOptionsList: this.transcriptDeliveryOptions,
                newOrderComments:
                    this.order.initialComments && this.order.initialComments.length ?
                    this.order.initialComments.map(comment =>
                    {
                        return {
                            commentHtml: comment.commentHtml,
                            commentType: comment.commentType,
                            createdDateTimeUTC: comment.createdDateTimeUTC,
                            createdByUserDisplayName: comment.createdByUserDisplayName,
                            viewOnly: true
                        };
                    }) :
                    this.order.clientOrderRequestId &&
                    !this.isUuidNullOrEmpty(this.order.clientOrderRequestId) &&
                    this.order.clientOrderRequest &&
                    this.order.clientOrderRequest.comments ? 
                    [{
                        viewOnly: true,
                        commentHtml: this.order.clientOrderRequest.commentsHtml,
                        commentType: 1,
                        createdByUserDisplayName: this.order.clientOrderRequest.submittedByUserAccount.displayName,
                        createdDateTimeUTC: this.order.clientOrderRequest.submittedDateTimeUTC
                    }] :
                    null,


                newOrderPayments: [],

                orderingClientId: this.isUuidNullOrEmpty(this.order.orderingClientId) ? null : this.order.orderingClientId,
                orderingClientBranchId: this.isUuidNullOrEmpty(this.order.orderingClientBranchId) ? null : this.order.orderingClientBranchId,
                selectableClientBranches: this.initialClientBranches,
                orderingClientContactId: this.order.orderingClientContactId,
                clientReference: this.order.clientReference,
                orderedDateTimeUTC: this.order.orderedDateTimeUTC,
                separateBillingContact: (this.initialBillingContact != null) || (this.isUuidNullOrEmpty(this.order.orderId) && this.promptForBillingContact),
                billingContactId: this.order.billingContactId,
                currentSelectableServiceTypes: this.selectableServiceTypes,
                serviceTypeId: this.isUuidNullOrEmpty(this.order.serviceTypeId) ? null : this.order.serviceTypeId,
                serviceTypeRequiresServiceDelay: (this.order.serviceType == null) ? false : this.order.serviceType.requiresServiceDelay,
                currentSelectableServiceDelays: this.selectableServiceDelays,
                serviceDelayId: this.order.serviceDelayId,
                transcriptDeliveryOptions: this.order.transcriptDeliveryOptions,
                transcriptDeliveryOptionsList: this.order.transcriptDeliveryOptionsList,
                currentSelectableNonProductionChargeTypes: this.selectableNonProductionChargeTypes,
                currentPossibleNonProductionChargeDates: this.possibleNonProductionDates,
                currentNonProductionCharges: this.nonProductionCharges,
                currentDefaultDeliveryContacts: this.defaultDeliveryContacts,
                currentDeliveryContactIds: this.deliveryContactIds,
                currentDeliveryContactCcIds: this.deliveryContactCcIds,
                currentDeliveryContactBccIds: this.deliveryContactBccIds,
                currentAttachments: this.attachments,
                allAsrEngineOptions: null,
                asrEngine: null,
                asrOptions: {},
                profanityOptions: ["None", "Removed", "Tags", "Masked"],
                creditAmount: this.order.creditAmount,

                selectedLogNotesSubStatus: this.order.logNotesSubStatusString,
                selectedAudioSubStatus: this.order.audioSubStatusString,
                selectedReleaseApprovalSubStatus: this.order.releaseApprovalSubStatusString,
                selectedDepositSubStatus: this.order.depositSubStatusString,
                selectedVettingSubStatus: this.order.vettingSubStatusString,
                serviceTypeHideDistributionList: false,

                selectedClient: null,
                orderingClientManagesAssignedContracts: this.initialClientManagesAssignedContracts,
            };
        },
        watch: {
            initialTranscriptDeliveryOptions: function () {
                this.transcriptDeliveryOptionsList = this.initialTranscriptDeliveryOptions;
            },
            separateBillingContact: function () {
                if (!this.separateBillingContact) {
                    this.billingContactId = null;
                }
            },
            selectableServiceTypes: function () {
                this.currentSelectableServiceTypes = this.selectableServiceTypes;
                Vue.nextTick().then(() => {
                    if (this.$refs.serviceTypeSelect) this.$refs.serviceTypeSelect.refresh();
                    if (this.$refs.serviceTypeSelectValidator) this.$refs.serviceTypeSelectValidator.reset();
                });
            },
            selectableServiceDelays: function () {
                this.currentSelectableServiceDelays = this.selectableServiceDelays;
                if (this.serviceTypeRequiresServiceDelay) {
                    Vue.nextTick().then(() => {
                        this.$refs.serviceDelaySelect.refresh();
                    });
                }
            },
            selectableNonProductionChargeTypes: function () {
                this.currentSelectableNonProductionChargeTypes = this.selectableNonProductionChargeTypes;
            },
            possibleNonProductionDates: function () {
                this.currentPossibleNonProductionChargeDates = this.possibleNonProductionDates;
            },
            asrEngine: function (value) {
                switch (value) {
                    case "Deepgram":
                        this.asrOptions = {
                            enableSpeakerSeparation: false,
                            combineTranscriptResult: true,
                        };
                        break;
                    case "AzureWhisper":
                        this.asrOptions = {
                            enableSpeakerSeparation: false,
                            combineTranscriptResult: true,
                            dictatedPunctuationMode: true,
                            automaticPunctuationMode: true,
                            profanityMode: null
                        };
                        break;
                }
            }
        },
        computed: {
            displayAsrOptions: function () {

                var displayOption = false;

                if (this.currentAttachments != null) {
                    this.currentAttachments.forEach(attachment => {
                        if (attachment.classificationString == "AudioForAsr") {
                            displayOption = true;
                        }
                    });
                }
                return displayOption;
            },
            mandatoryContactProperties: function () {
                if (!this.$config.enableRegionStateOptions) {
                    return ['address', 'suburb', 'country'];
                }
                else {
                    return ['address', 'suburb', 'state', 'postcode', 'country'];
                }
            },
            isICMS: function () {
                return this.order.externalSource != null;
            }
        },
        methods: {
            valueExists: function (values) {
                var exists = false;
                values.forEach(e => {
                    if (e !== undefined)
                        exists = true;
                });
                return exists;
            },
            loadAsrEngineOptions: function () {
                var url = this.$urlHelper.getApiUrl('asrrequest', 'enginelist');
                axios
                    .get(url)
                    .then((response) => {
                        this.allAsrEngineOptions = response.data;
                        this.asrEngine = (this.allAsrEngineOptions && this.allAsrEngineOptions.length == 1) ? this.allAsrEngineOptions[0].value : null;
                    })
                    .catch((ex) => {
                        var errors = this.handleAxiosException(ex);
                        this.showErrorsToast(errors);
                    });
            },
            updateDepositSubStatus: function () {
                if (this.newOrderPayments.length > 0) {
                    var requestedPayment = this.newOrderPayments.find(p => !p.isReceivedPayment);
                    if (requestedPayment) {
                        if (this.selectedDepositSubStatus == null) this.selectedDepositSubStatus = "DepositRequired";
                        else if ((this.selectedDepositSubStatus == "DepositReceived") || (this.selectedDepositSubStatus == "PaidInFull")) this.selectedDepositSubStatus = "FurtherPaymentRequired";
                    }
                }
            },
            apiControllerUrl: function (apiRelativeUrl) {
                var url = this.apiBaseUrl;
                if (!url.endsWith('/')) url += '/';
                url += apiRelativeUrl;
                return url;
            },
            orderingClientSelected: function () {
                this.$emit('ordering-client-change', this.orderingClientId);

                if (!this.isUuidNullOrEmpty(this.orderingClientId)) {
                    this.loadClient();
                }

                this.reloadClientBranches();
            },
            serviceTypeSelected: function (serviceType) {
                if (serviceType != null) {
                    //console.log(JSON.stringify(serviceType));
                    this.serviceTypeRequiresServiceDelay = serviceType.requiresServiceDelay;
                    if (!this.serviceTypeRequiresServiceDelay) {
                        this.serviceDelayId = null;
                    }

                    this.serviceTypeHideDistributionList = serviceType.hideDistributionList;
                }
                else {
                    this.serviceTypeRequiresServiceDelay = false;
                    this.serviceDelayId = null;
                    this.serviceTypeHideDistributionList = false;
                }

                if (this.serviceTypeHideDistributionList == null) {
                    this.serviceTypeHideDistributionList = false;
                }

                console.log(JSON.stringify('hideDistributionList: ' + this.hideDistributionList));
                console.log(JSON.stringify('serviceTypeHideDistributionList: ' + this.serviceTypeHideDistributionList));

                this.$emit('service-type-change', serviceType);
            },
            reloadClientBranches: function () {
                if (this.orderingClientId) {
                    var url = this.$urlHelper.getApiUrl('clientbranch', 'byclient', [this.orderingClientId]);
                    axios
                        .get(url)
                        .then((response) => {
                            this.selectableClientBranches = response.data;
                            Vue.nextTick().then(() => {
                                if (this.selectableClientBranches != null && this.selectableClientBranches.length == 1) {
                                    this.orderingClientBranchId = this.selectableClientBranches[0].clientBranchId;
                                }
                                this.$refs.orderingClientBranchValidator.reset();
                            })
                        })
                        .catch((ex) => {
                            var errors = this.handleAxiosException(ex);
                            this.showErrorsToast(errors);
                        });
                } else {
                    this.selectableClientBranches = null;
                }
            },

            loadClient: function () {
                if (this.orderingClientId) {
                    var url = this.$urlHelper.getApiUrl('client', 'byclient', [this.orderingClientId]);
                    axios
                        .get(url)
                        .then((response) => {
                            this.selectedClient = response.data;

                            if (this.selectedClient != null) {
                                this.separateBillingContact = this.selectedClient.promptForBillingContact;
                                this.orderingClientManagesAssignedContracts = this.selectedClient.managesAssignedContracts;
                            } else {
                                this.orderingClientManagesAssignedContracts = false;
                            }
                        })
                        .catch((ex) => {
                            var errors = this.handleAxiosException(ex);
                            this.showErrorsToast(errors);
                        });
                } else {
                    this.selectedClient = null;
                    this.orderingClientManagesAssignedContracts = false;
                }
            },

            //findClientBranches: function (params, success, failure) {
            //    var filter = {
            //        searchTerm: ''
            //    };
            //    if (typeof (params.data.term) != "undefined")
            //        filter.searchTerm = params.data.term;
            //    var url = this.apiControllerUrl('clientbranch');
            //    url += '/client/' + this.orderingClientId + '/find';
            //    axios
            //        .post(url, JSON.stringify(filter),
            //            {
            //                headers: {
            //                    'Content-Type': 'application/json'
            //                }
            //            })
            //        .then(function (response) {
            //            var mappedData = response.data;
            //            if (mappedData != null) {
            //                mappedData = $.map(mappedData, function (obj) {
            //                    obj.id = obj.clientBranchId;
            //                    obj.text = obj.name;
            //                    return obj;
            //                });
            //            }
            //            success({ results: mappedData });
            //        })
            //        .catch(function (error) {
            //            failure(error);
            //        });
            //},
            //reload: function () {
            //    var assignmentsVisible = (this.canViewAllDetails || this.canManageAssignments) && (this.workItem != null);
            //    var commentsVisible = (this.workItem != null);
            //    if (this.relatedCourtMatterId) {
            //        if (this.$refs.courtMatterDetails) this.$refs.courtMatterDetails.reload();
            //        if (this.$refs.courtAppearances) this.$refs.courtAppearances.reload();
            //    }
            //    if (this.relatedCourtSittingId && this.$refs.courtSittingDetails) this.$refs.courtSittingDetails.reload();
            //    if (this.relatedInterviewId && this.$refs.interviewDetails) this.$refs.interviewDetails.reload();
            //    if (assignmentsVisible && this.$refs.staffWorkItemAssignments) this.$refs.staffWorkItemAssignments.reload();
            //    if (commentsVisible && this.$refs.comments) this.$refs.comments.reload();
            //    if (this.canViewAttachments && this.$refs.workItemAttachments) this.$refs.workItemAttachments.reload();
            //}
            trySaveOrder: function (actionAfterSave) {
                if (this.currentAttachments) {
                    this.currentAttachments.forEach((attachment) => {
                        if (attachment.classificationString == null) {
                            this.showErrorsToast(["Not all attachments have a classification."]);
                            return;
                        }
                    });
                }


                this.asrOptions.asrEngine = this.asrEngine
                this.$refs.validationObserver
                    .validate()
                    .then((isValid) => {
                        if (isValid) {
                            if (this.validate != null) {
                                isValid = this.validate();
                            }
                            if (isValid) {
                                //console.log('selected transcript delivery options');
                                //console.log(JSON.stringify(this.transcriptDeliveryOptions));
                                var data = {
                                    order: {
                                        orderingClientId: this.orderingClientId,
                                        orderingClientBranchId: this.orderingClientBranchId,
                                        orderingClientContactId: this.orderingClientContactId,
                                        clientReference: this.clientReference,
                                        orderedDateTimeUTC: this.orderedDateTimeUTC,
                                        billingContactId: this.billingContactId,
                                        serviceTypeId: this.serviceTypeId,
                                        serviceDelayId: this.serviceDelayId,
                                        creditAmount: this.creditAmount,
                                    },
                                    nonProductionCharges: [],
                                    newPayments: [],
                                    attachments: [],

                                    asrOptions: this.asrOptions,

                                    logNotesSubStatus: this.selectedLogNotesSubStatus,
                                    audioSubStatus: this.selectedAudioSubStatus,
                                    releaseApprovalSubStatus: this.selectedReleaseApprovalSubStatus,
                                    depositSubStatus: this.selectedDepositSubStatus,
                                    vettingSubStatus: this.selectedVettingSubStatus,
                                    transcriptDeliveryOptions: this.transcriptDeliveryOptionsList,
                                }

                                if (!this.hideDistributionList && !this.serviceTypeHideDistributionList) {
                                    data.deliveryContactIds = this.currentDeliveryContactIds
                                    data.deliveryContactCcIds = this.currentDeliveryContactCcIds
                                    data.deliveryContactBccIds = this.currentDeliveryContactBccIds
                                }
                                else {
                                    data.deliveryContactIds = [];
                                    data.deliveryContactCcIds = [];
                                    data.deliveryContactBccIds = [];
                                }

                                if (!this.isUuidNullOrEmpty(this.order.orderId)) data.order.orderId = this.order.orderId;
                                if (!this.isUuidNullOrEmpty(this.order.clientOrderRequestId)) data.order.clientOrderRequestId = this.order.clientOrderRequestId;
                                if (!this.isUuidNullOrEmpty(this.order.quoteId)) data.order.quoteId = this.order.quoteId;
                                if (this.currentNonProductionCharges) {
                                    this.currentNonProductionCharges.forEach((nonProductionCharge) => {
                                        data.nonProductionCharges.push({
                                            nonProductionChargeId: nonProductionCharge.nonProductionChargeId,
                                            nonProductionChargeTypeId: nonProductionCharge.nonProductionChargeTypeId,
                                            quantity: nonProductionCharge.quantity,
                                            splitPercentage: nonProductionCharge.splitPercentage,
                                            unitCost: nonProductionCharge.unitCost,
                                            totalCost: nonProductionCharge.totalCost,
                                            chargeable: nonProductionCharge.chargeable,
                                            comments: nonProductionCharge.comments,
                                            relatedToDates: nonProductionCharge.relatedToDates,
                                            relatedToAllDates: nonProductionCharge.relatedToAllDates
                                        });
                                    });
                                }
                                if (this.newOrderComments) {
                                    data.newOrderComments = [];
                                    this.newOrderComments.forEach((comment) => {
                                        if (!comment.viewOnly)
                                            data.newOrderComments.push({
                                                commentHtml: comment.commentHtml,
                                                commentType: comment.commentType
                                            });
                                    });
                                }
                                if (this.newOrderPayments) {
                                    this.newOrderPayments.forEach((newPayment) => {
                                        var apiNewPayment = {
                                            isReceivedPayment: newPayment.isReceivedPayment,
                                            description: newPayment.description,
                                            amount: newPayment.amount,
                                            amountIncludesGst: newPayment.amountIncludesGst
                                        };
                                        if (newPayment.dueDateTimeUTC) {
                                            apiNewPayment.dueDateTimeUTC = this.formatDateTime(newPayment.dueDateTimeUTC, 'YYYY-MM-DDTHH:mm:ss', true, true);
                                        }
                                        if (newPayment.receivedDateTimeUTC) {
                                            apiNewPayment.receivedDateTimeUTC = this.formatDateTime(newPayment.receivedDateTimeUTC, 'YYYY-MM-DDTHH:mm:ss', true, true);
                                        }
                                        data.newPayments.push(apiNewPayment);
                                    });
                                }
                                if (this.currentAttachments) {
                                    this.currentAttachments.forEach((attachment) => {
                                        data.attachments.push({ fileId: attachment.fileId, classification: attachment.classificationString, copyOnSave: attachment.copyOnSave });
                                    });
                                }

                                this.save(data)
                                    .then((response) => {
                                        var url = this.returnUrl;

                                        if (actionAfterSave == 'new') {
                                            url = this.createOrderUrl;
                                        }
                                        else if (actionAfterSave == 'edit') {
                                            url = this.editOrderUrlTemplate.replace(this.emptyUuid, response.data.orderId);
                                        }
                                        window.location.replace(url);
                                    })
                                    .catch((ex) => {
                                        var errors = this.handleAxiosException(ex);
                                        this.showErrorsToast(errors);
                                    });
                            }
                        }
                        else this.$refs.validationSummary.activate();
                    });
            }
        },
        mounted: function () {
            this.loadAsrEngineOptions();
        }
    }
</script>



